import { useEffect, useRef } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { serviceQueryKey } from '../helpers/queryClient';

const sameRefresh = (a, b) => a.length === b.length && a.every((value, i) => Object.is(value, b[i]));

/**
 * The cached fetch behind every service hook (`campaignService.GetAllCampaigns`
 * and friends).
 *
 * - `loading` is true only while there is nothing to show for this query yet.
 *   Data already cached (this session or restored after a reload) shows at
 *   once and is refetched quietly; `refreshing` says a refetch is in flight.
 * - `refresh` is the old reload signal (AuthContext's `fetch` toggle, a local
 *   refresh key, or an array of them). When it changes after mount the query
 *   refetches in the background, keeping the current data on screen.
 * - While `enabled` is false the query waits, and `loading` stays true, the
 *   same as before the cache existed.
 * - `keepPrevious` keeps the last answer on screen while a new query (another
 *   page, filter or search) loads; `placeholder` is true while it does.
 * - `persist: false` keeps a response in memory only, never in localStorage
 *   (secrets such as API keys).
 */
const useServiceQuery = ({
  name,
  params = null,
  queryFn,
  refresh,
  enabled = true,
  keepPrevious = false,
  persist = true,
}) => {
  const query = useQuery({
    queryKey: serviceQueryKey(name, params),
    queryFn,
    enabled,
    placeholderData: keepPrevious ? keepPreviousData : undefined,
    meta: { persist },
  });

  const refreshList = Array.isArray(refresh) ? refresh : [refresh];
  const lastRefresh = useRef(refreshList);
  const { refetch } = query;
  useEffect(() => {
    if (sameRefresh(lastRefresh.current, refreshList)) return;
    lastRefresh.current = refreshList;
    // cancelRefetch: false joins a fetch already in flight instead of restarting it.
    if (enabled) refetch({ cancelRefetch: false });
  });

  return {
    data: query.data,
    loading: query.isPending,
    error: query.isError,
    refreshing: query.isFetching && !query.isPending,
    placeholder: query.isPlaceholderData,
  };
};

export default useServiceQuery;
